import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ShiftDayBreakGet, ShiftDayBreakParams, ShiftService } from '@app/services/shift.service';

@Component({
  selector: 'tt-shift-day-break',
  templateUrl: './shift-day-break.component.html',
  styleUrls: ['./shift-day-break.component.css'],
  standalone: false,
})
export class ShiftDayBreakComponent implements OnInit {
    public page?: ShiftDayBreakGet;
    public params: ShiftDayBreakParams = { shift_id: '', dayno: '', shiftbreak_keyno: '0' };
    public saving = false;
    public deleting = false;
    public validating = false;
    private validationVersion = 0;
    private validationRequested = false;
    private validation?: Promise<void>;

    constructor(
        private readonly shift: ShiftService,
        private readonly modal: ModalService,
        private readonly translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) { this.navigator = useNavigatorFor(navigator, this); }

    public async ngOnInit(): Promise<void> {
        this.params = { shift_id: String(this.navigator?.params['shift_id'] ?? ''), dayno: String(this.navigator?.params['dayno'] ?? ''), shiftbreak_keyno: String(this.navigator?.params['shiftbreak_keyno'] ?? '0') };
        const [page, weekDays] = await Promise.all([this.shift.getDayBreak(this.params), this.shift.getWeekDays({ shift_id: this.params.shift_id })]);
        page.day_name = weekDays.find(day => String(day.item_id) === this.params.dayno)?.item_name ?? '';
        this.page = page;
    }

    public async onTimeChanged(value: string, field: 'from_time' | 'to_time'): Promise<void> {
        if (!this.page || this.page[field] === value) return;
        this.page[field] = value;
        ++this.validationVersion;
        this.validationRequested = !!value.trim();
        if (!this.validationRequested) return;
        this.validation ??= this.validateHours();
        try { await this.validation; } finally { this.validation = undefined; }
    }

    private async validateHours(): Promise<void> {
        this.validating = true;
        try {
            let version: number;
            do {
                version = this.validationVersion;
                const response = await this.shift.validateBreakHours({ ...this.page! });
                if (version === this.validationVersion && this.page) this.page.breakhour = response.breakhour;
            } while (version !== this.validationVersion && this.validationRequested);
        } finally { this.validating = false; }
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.saving || this.deleting || this.validating) return;
        this.saving = true;
        try {
            const response = await this.shift.saveDayBreak({ ...this.page, shift_id: this.params.shift_id, dayno: this.params.dayno });
            if (String(response.status_no) !== '0') await this.showWarning(response.status_message);
            else await this.navigator?.back();
        } finally { this.saving = false; }
    }

    public async deleteValues(): Promise<void> {
        if (!this.page || this.deleting || this.saving) return;
        this.deleting = true;
        try {
            if (!await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', translateTitle: true, message: 'confirm_message', translateMessage: true, ok: 'confirm_ok_label', cancel: 'confirm_cancel_label' })) return;
            const response = await this.shift.deleteDayBreak({ shiftbreak_keyno: this.page.shiftbreak_keyno });
            if (String(response.status_no) !== '0') await this.showWarning(response.status_message);
            else await this.navigator?.back();
        } finally { this.deleting = false; }
    }

    private async showWarning(message: string): Promise<void> {
        await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message, buttons: [{ label: await this.translate.translate('ok'), cssClass: 'btn-warning', close: true }] });
    }
}
